import { z } from 'zod'

/**
 * Schema for creating a new client.
 * - name: required
 * - contractStart: required, YYYY-MM-DD format
 * - contractEnd: optional ISO timestamp
 * - apiKey: optional (will be encrypted if provided)
 * - keyExpiration: optional ISO timestamp
 */
export const createClientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  contractStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format'),
  contractEnd: z.string().nullable().optional(),
  apiKey: z.string().nullable().optional(),
  keyExpiration: z.string().nullable().optional(),
})

export type CreateClientInput = z.infer<typeof createClientSchema>

/**
 * Schema for updating client details.
 * All fields optional - only provided fields will be updated.
 * Does NOT include apiKey (use updateClientKeySchema for that).
 * trustCenterEnabled is how an internal user puts a client the sync switched
 * off (Drata answered 402) back into the Trust Center pull.
 */
export const updateClientDetailsSchema = z.object({
  name: z.string().min(1).optional(),
  contractStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format')
    .optional(),
  contractEnd: z.string().nullable().optional(),
  keyExpiration: z.string().nullable().optional(),
  trustCenterEnabled: z.boolean().optional(),
})

export type UpdateClientDetailsInput = z.infer<typeof updateClientDetailsSchema>

/**
 * Schema for updating/adding a client's API key.
 * - apiKey: required
 * - keyExpiration: optional ISO timestamp
 */
export const updateClientKeySchema = z.object({
  apiKey: z.string().min(1, 'API key is required'),
  keyExpiration: z.string().nullable().optional(),
})

export type UpdateClientKeyInput = z.infer<typeof updateClientKeySchema>
